import { useLayoutEffect, useState } from 'react';
import ExcelAttendanceUploadPopup from 'components/popup/ExcelAttendanceUploadPopup';
import { useNavigate } from 'react-router';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { adminQueryKeyState } from 'recoil/adminpopup/adminQueryKeyState';
import { useUserState } from 'recoil/useUserState';
import { DropMenu, DropMenuItem } from '.';
import { MenuItem } from './MenuItem';
import { Link } from 'react-router-dom';
import { useMutation, useQueries } from '@tanstack/react-query';
import { getDataQuery } from '../../../api';
import * as XLSX from 'xlsx-js-style';
import { applyExcelStyles, applyCenterAlignment, applyAbsenceFontColor, writeExcelFile } from 'util/excelStyle';
import {subjectOptionDataState} from "../../../recoil/subject/subjectOptionDataState";
import {sisCourseState} from "../../../recoil/subject/sisCourseState";
import {modules} from "../../common/board/EditorToolbar";
import moment from "moment";
import { downloadLoadingState } from 'recoil/loading/downloadLoadingState';

export function Sidebar(props) {
  const [queryKeyState, setQueryKeyState] = useRecoilState(adminQueryKeyState);

  const loginUser = useRecoilValue(useUserState);

  const [accountIndex, setAccountIndex] = useState();
  const [visibleExcelUploadPopup, setVisibleExcelUploadPopup] = useState(false);
  const [supportingIndex, setSupportingIndex] = useState();
  const [tapIndex, setTapIndex] = useState(-1);
  const [reportTapIndex, setReportTapIndex] = useState(-1);
  const [subjectState, setSubjectState] = useRecoilState(subjectOptionDataState);
  const sisCourseIdState = useRecoilValue(sisCourseState);
  const [downloadAllGradeQuery, downloadAllAttendanceQuery, downloadAllSurveyQuery] = useQueries({
    queries: [
      {
        queryKey: ['downloadAllGrade', subjectState.openYy, subjectState.openTmGbn],
        queryFn: async () => {
          const allGradeList = await getDataQuery(`/course/all/student/grade?openYy=${subjectState.openYy}&openTmGbn=${subjectState.openTmGbn}`);
          const body = allGradeList?.data ?? allGradeList;
          const data = body?.data ?? body;

          return Array.isArray(data) ? data:[];
        },
        enabled: false,
      },
      {
        queryKey: ['downloadAllAttendance', subjectState.openYy, subjectState.openTmGbn],
          queryFn: async () => {
            const allAttendanceList = await getDataQuery(`/attendance/course/all/student/online?openYy=${subjectState.openYy}&openTmGbn=${subjectState.openTmGbn}`);
            const body = allAttendanceList?.data ?? allAttendanceList;
            const data = body?.data ?? body;

            return Array.isArray(data) ? data:[];
          },
          enabled: false,
        },
        {
          queryKey: ['downloadAllSurvey', subjectState.openYy, subjectState.openTmGbn],
          queryFn: async () => {
            const allSurveyList = await getDataQuery(`/course/all/survey?openYy=${subjectState.openYy}&openTmGbn=${subjectState.openTmGbn}`);
            const body = allSurveyList?.data ?? allSurveyList;
            if(typeof body === 'string') return JSON.parse(body);
            return body;
          },
          enabled: false,
        },
    ],
  });
  const getTimestamp = () => moment().format('YYMMDD_HHmm');
  const termName = subjectState.termName || '';
  const normalizeAoa = (value) => {
    if (!Array.isArray(value)) return [];
    return Array.isArray(value[0]) ? value : [value];
  };

  const setDownloadLoading = useSetRecoilState(downloadLoadingState);
  const handleDownloadAllSurvey = async (e) => {
      if(!subjectState.openYy || !subjectState.openTmGbn){
          alert('학기가 선택되지 않았습니다.');
          return;
      }
    e?.preventDefault();
    setDownloadLoading(true);
    try{
      const res = await downloadAllSurveyQuery.refetch();
      const data = res?.data ?? res;
      const sheets = Array.isArray(data?.sheets) ? data.sheets : [];
      if(!sheets.length){
          alert('다운로드할 설문 데이터가 존재하지 않습니다.');
          return;
      }

      const wb = XLSX.utils.book_new();
      const used = new Set();

      sheets.forEach((s, idx) => {
          const sheetName = uniqueSheetName(s.sheetName || `sheet_${idx+1}`, used);
          const header = normalizeAoa(s?.header);
          const rows = normalizeAoa(s?.rows);
          const aoa = [...header, ...rows];
          const ws = XLSX.utils.aoa_to_sheet(aoa.length ? aoa : [['']]);
          applyExcelStyles(ws, 1);
          if (sheetName.endsWith('_통계') && aoa.length > 1) {
              const merges = [];
              [0, 1, 2].forEach(col => {
                  let start = 1;
                  for (let r = 2; r <= aoa.length; r++) {
                      const cur = r < aoa.length ? aoa[r][col] : null;
                      if (cur !== aoa[start][col]) {
                          if (r - 1 > start) merges.push({ s: { r: start, c: col }, e: { r: r - 1, c: col } });
                          start = r;
                      }
                  }
              });
              if (merges.length) {
                  ws['!merges'] = merges;
                  merges.forEach(merge => {
                      const cellRef = XLSX.utils.encode_cell({ r: merge.s.r, c: merge.s.c });
                      if (!ws[cellRef]) ws[cellRef] = { v: '', t: 's' };
                      ws[cellRef].s = { ...ws[cellRef].s, alignment: { vertical: 'top' } };
                  });
              }
          }
          XLSX.utils.book_append_sheet(wb, ws, sheetName);
      });

      writeExcelFile(wb, `전체_설문_${termName}_${getTimestamp()}.xlsx`);
    }catch (err){
        console.error(err);
        alert('설문 다운로드 중 오류가 발생했습니다.');
    } finally {
        setDownloadLoading(false);
    }
  }

  const handleDownloadAllGrade = async (e) => {
      console.log("start");
      if(!subjectState.openYy || !subjectState.openTmGbn){
          alert('학기가 선택되지 않았습니다.');
          return;
      }
    e?.preventDefault();
    setDownloadLoading(true);
    try{
      const {data} = await downloadAllGradeQuery.refetch();
      const gradeList = data ?? [];

      if(!gradeList.length){
        alert('다운로드할 성적 데이터가 존재하지 않습니다.');
        return;
      }

      const rows = gradeList.map((g, idx) => ({
        번호 : idx + 1,
        학번 : g.userNumber ?? '',
        이름 : g.userName ?? g.name ?? '',
        학과 : g.departmentName ?? '',
        지도교수 : g.tutorName || '미배정',
        총점 : g.totalScore ?? '',
        출석 : g.attendanceCount ?? '',
        미출석 : ((g.totalAttendanceCount ?? 0) - (g.attendanceCount ?? 0)),
        최종등급 : g.totalGrade === true ? 'PASS' : g.totalGrade === false ? 'NON_PASS' : (g.totalGrade ?? ''),
      }));

      const ws = XLSX.utils.json_to_sheet(rows);
      applyExcelStyles(ws, 1);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Grades');

      writeExcelFile(wb, `전체_성적_${termName}_${getTimestamp()}.xlsx`);
    } catch(err){
      console.error(err);
      alert('성적 다운로드 중 오류가 발생했습니다.');
    } finally {
      setDownloadLoading(false);
    }
  };

    const state = (s) => {
        // eslint-disable-next-line no-empty
        switch (s){
            case 'ATTENDANCE':
                return '출석';
            case 'LATE':
                return '지각';
            case 'EXCUSED_ABSENCE':
                return '공결';
            case 'ABSENCE':
                return '결석';
            default:
                return '';
        }
    }

    const handleDownloadAllAttendance = async (e) => {
        console.log("start");
        if(subjectState.termId === undefined){
          alert('학기가 선택되지 않았습니다.');
          return;
        }
        e?.preventDefault();
        setDownloadLoading(true);
        try{
            const {data} = await downloadAllAttendanceQuery.refetch();
            const attendanceList = data ?? [];
            
            if(!attendanceList.length){
                alert('출석데이터가 존재하지 않습니다.');
                return;
            }

            const weeks = Array.from({length: 15}, (_, i) => String(i+1));
            const maxWeek = weeks.reduce((a, w) => {
                a[w] = 0;
                return a;
            },{});

            attendanceList.forEach((user) =>{
                (user.attendanceSummaryDtoList || []).forEach((m) => {
                    const w = String(m.moduleNumber ?? m.week ?? '');
                    const cnt = m.canvasAttendanceStates?.length || 0;
                    if(w && maxWeek[w] != null){
                        maxWeek[w] = 3;
                    }
                });
            });

            const wsData = [];
            // 헤더 생성
            const headerRow1 = ['번호', '학번', '이름', '학과', '지도교수', '출석', '미출석'];
            // 2행 (번호~미출석은 병합할 거라 비워둠)
            const headerRow2 = ['', '', '', '', '', '', ''];

            weeks.forEach((w) => {
                const cnt = maxWeek[w] || 0;
                for (let i = 0; i < cnt; i++) {
                    headerRow1.push(`${w}주차`); // 위 줄: 주차
                    headerRow2.push(`${i + 1}차시`);// 아래 줄: 차시
                }
            });

            wsData.push(headerRow1);
            wsData.push(headerRow2);

            attendanceList.forEach((user, idx) => {
                const baseRow = [
                    idx + 1,
                    user.userNumber,
                    user.name,
                    user.departmentName ?? '',
                    user.tutorName || '미배정',
                    user.attendanceCnt,
                    user.absenceCnt,
                ];

                const byWeek = new Map();
                (user.attendanceSummaryDtoList || []).forEach((m) => {
                    const w = String(m.moduleNumber ?? m.week ?? '');
                    byWeek.set(w, m.canvasAttendanceStates || []);
                });

                const detailCells = [];

                weeks.forEach((w) => {
                    const states = byWeek.get(w) || [];
                    const cnt = maxWeek[w] || 0;

                    for(let i = 0; i < cnt; i++){
                        detailCells.push(state(states[i]?.moduleState));
                    }
                });

                wsData.push([...baseRow, ...detailCells]);
            });

            // 워크시트 생성
            const ws = XLSX.utils.aoa_to_sheet(wsData);
            const merges = [];

            for (let c = 0; c < 7; c++) {
                merges.push({ s: { r: 0, c }, e: { r: 1, c } });
            }

            let col = 7; // 0:번호,1:학번,2:이름,3:학과,4:지도교수,5:출석,6:미출석 → 다음부터 주차
            weeks.forEach((w) => {
                const cnt = maxWeek[w] || 0;
                if(cnt > 1){
                    merges.push({
                        s: { r: 0, c: col },
                        e: { r: 0, c: col + cnt - 1 },
                    });
                }
                col += cnt;
            });

            ws['!merges'] = merges;
            applyExcelStyles(ws, 2);
            applyCenterAlignment(ws, 7);
            applyAbsenceFontColor(ws, 2);

            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Attendance');

            // 파일 다운로드
            writeExcelFile(wb, `전체_출석_${termName}_${getTimestamp()}.xlsx`);
        }catch (err){
            console.error(err);
            alert('다운로드 중 오류가 발생했습니다.');
        } finally {
            setDownloadLoading(false);
        }
    };

    function sheetName(name){
        if(!name) return 'sheet';
        let x = String(name)
            .replace(/[\\/?*[\]]/g, ' ')
            .replace(/:+/g, ' ')
            .trim();
        if(!x) x = 'sheet';
        return x.length > 31 ? x.slice(0, 31) : x;
    }

    function uniqueSheetName(base, use){
      const cleaned = sheetName(base);
      let date = cleaned;
      let i = 1;
      while (use.has(date)){
        i += 1;
        const suffix = `_${i}`;
        const max = 31 - suffix.length;
        const cut = cleaned.length > max ? cleaned.slice(0, Math.max(1, max)) : cleaned;
        date = `${cut}${suffix}`;
      }

      use.add(date);
      return date;
    }

  const navigate = useNavigate();

  // 새로고침시 사이드바 액티브 상태 수정
  const pathname = window.location.pathname;
  useLayoutEffect(() => {
    if (pathname.includes('/activity')) {
      setSupportingIndex(-1);
      setReportTapIndex(-1);
      return setTapIndex(1);
    }
    if (pathname.includes('/attendance')) {
      setSupportingIndex(-1);
      setReportTapIndex(-1);
      return setTapIndex(2);
    }
    // if (pathname.includes('/zoom')) return setTapIndex(3);
    if (pathname.includes('/report')) {
      setSupportingIndex(-1);
      setReportTapIndex(-1);
      return setTapIndex(3);
    }
    if (pathname.includes('/useraccounts')) {
      setTapIndex(4);
      setAccountIndex(0);
      setSupportingIndex(-1);
      setReportTapIndex(-1);
      return;
    }
    if (pathname.includes('/managementboard')) {
      setTapIndex(4);
      setAccountIndex(1);
      setSupportingIndex(-1);
      setReportTapIndex(-1);
      return;
    }

    if (pathname.includes('/department')) {
      setTapIndex(8);
      setReportTapIndex(0);
      setSupportingIndex(-1);
      setAccountIndex(-1);
      return;
    }
    if (pathname.includes('/student')) {
      setTapIndex(8);
      setReportTapIndex(1);
      setSupportingIndex(-1);
      setAccountIndex(-1);
      return;
    }

    if (pathname.includes('/notice')) {
      setSupportingIndex(-1);
      setReportTapIndex(-1);
      return setTapIndex(12);
    }
    if (pathname.includes('/faq')) {
      setSupportingIndex(-1);
      setReportTapIndex(-1);
      return setTapIndex(13);
    }
    if (pathname.includes('/reference')) {
      setSupportingIndex(-1);
      setReportTapIndex(-1);
      return setTapIndex(14);
    }
    if (pathname.includes('/manual')) {
      setSupportingIndex(-1);
      setReportTapIndex(-1);
      return setTapIndex(15);
    }

    if (pathname.includes('/grade')) {
      setSupportingIndex(-1);
      setReportTapIndex(-1);
      return setTapIndex(11);
    }

    if (pathname.includes('/monitoring')) {
      setSupportingIndex(-1);
      setReportTapIndex(-1);
      setAccountIndex(-1);
      return setTapIndex(16);
    }

    if (pathname.includes('/setting')) return setTapIndex(6);
    return setTapIndex(0);
  }, [navigate, pathname]);

  const handlePageNumberReset = () => {
    setQueryKeyState({ ...queryKeyState, currentPage: 1 });
  };
  return (
    <>
      {visibleExcelUploadPopup && (
        <ExcelAttendanceUploadPopup openYy={subjectState.openYy} openTmGbn={subjectState.openTmGbn} onClose={() => setVisibleExcelUploadPopup(false)} />
      )}
      <aside id='sidebar' className='sidebar'>
        <div className=''>
          {/* 홈 */}
          <div className='mt-2'>

            <ul>
              <div className='d-flex justify-content-center align-items-center mb-4 '>
                <Link to='/' className='d-flex justify-content-center'>
                  <img src='/assets/img/logo.png' alt='' 
                  style={{ height: "100%", width: "100%" }}/>
                </Link>
              </div>
            </ul>

            <ul>
              {/* 홈 */}
              <MenuItem
                link={'/'}
                icon={'myclass_icon.png'}
                title={'My Class'}
                setTapIndex={setTapIndex}
                tapIndex={0}
                active={tapIndex === 0}
              />
              {/* 활동 */}
              {/* <MenuItem
                link={'/activity'}
                icon={'bi-collection-fill'}
                title={'활동'}
                setTapIndex={setTapIndex}
                tapIndex={1}
                active={tapIndex === 1}
              /> */}
            </ul>

            {/* 학습현황 */}

              <ul>
              {/* {loginUser?.role === 'ADMIN' && (
                <>
                  <MenuItem
                    link={'/attendance'}
                    icon={'lnb_attendance_icon.png'}
                    title={'출결현황'}
                    setTapIndex={setTapIndex}
                    tapIndex={2}
                    active={tapIndex === 2}
                  />

                  <MenuItem
                    link={'/grade'}
                    icon={'lnb_grades_icon.png'}
                    title={'성적현황'}
                    setTapIndex={setTapIndex}
                    tapIndex={11}
                    active={tapIndex === 11}
                  />
                </>
              )} */}

                {loginUser?.role === 'STUDENT' && (
                  <>
                    {/* <MenuItem
                      link={'/grade'}
                      icon={'bi-easel3-fill'}
                      title={'성적현황'}
                      setTapIndex={setTapIndex}
                      tapIndex={11}
                      active={tapIndex === 11}
                    />
                  <MenuItem
                    link={'/report'}
                    icon={'bi-bar-chart-fill'}
                    title={'성과 보고서'}
                    setTapIndex={setTapIndex}
                    tapIndex={3}
                    active={tapIndex === 3}
                  /> */}
                  </>
                )}

                {/* <MenuItem
                  link={'/zoom'}
                  icon={'bi-pip-fill'}
                  title={'ZOOM 학습현황'}
                  setTapIndex={setTapIndex}
                  tapIndex={3}
                  active={tapIndex === 3}
                />
                */}
              <MenuItem
                link={'/notice'}
                icon={'ctlannouncement_icon.png'}
                title={'공지사항'}
                setTapIndex={setTapIndex}
                tapIndex={12}
                active={tapIndex === 12}
              />


              {/* <MenuItem
                link={'/reference'}
                icon={'bi-gear-fill'}
                title={'자료실'}
                setTapIndex={setTapIndex}
                tapIndex={14}
                active={tapIndex === 14}
              /> */}

              <MenuItem
                link={'/manual'}
                icon={'manual_icon.png'}
                title={'매뉴얼'}
                setTapIndex={setTapIndex}
                tapIndex={15}
                active={tapIndex === 15}
              />

                {/*<MenuItem
                link={'/faq'}
                icon={'faq_icon.png'}
                title={'Q&A'}
                setTapIndex={setTapIndex}
                tapIndex={13}
                active={tapIndex === 13}
              />*/}
              </ul>

              {loginUser?.role !== 'STUDENT' && (
                  <>
                    {/* <MenuItem
                      link={'/grade'}
                      icon={'bi-easel3-fill'}
                      title={'성적현황'}
                      setTapIndex={setTapIndex}
                      tapIndex={11}
                      active={tapIndex === 11}
                    /> */}
                    {/* <DropMenu
                      icon={'bi-bar-chart-fill'}
                      title={'성과보고서'}
                      setTapIndex={setTapIndex}
                      tapIndex={8}
                      active={tapIndex === 8}
                      bold
                    >
                      <DropMenuItem
                        link={'/department'}
                        title={'학과 성과보고서'}
                        tapIndex={0}
                        active={reportTapIndex === 0}
                      />
                      <DropMenuItem
                        link={'/student'}
                        title={'학생 성과보고서'}
                        tapIndex={1}
                        active={reportTapIndex === 1}
                      />
                    </DropMenu> */}
                  </>
                )}  

            {/* 관리현황 */}
            {loginUser?.role === 'ADMIN' && (
              <div>
                <div className='mt-3 fw-bold fz-12'>
                  <h3 className='px-3 py-2'>관리현황</h3>
                </div>
                <MenuItem
                  link={'/monitoring'}
                  icon={'lnb_grades_icon.png'}
                  title={'모니터링'}
                  setTapIndex={setTapIndex}
                  tapIndex={16}
                  active={tapIndex === 16}
                />

                <DropMenu
                  icon={'bi-question-circle-fill'}
                  title={'계정관리'}
                  setTapIndex={setTapIndex}
                  tapIndex={4}
                  active={tapIndex === 4}
                >
                  <DropMenuItem
                    link={'/useraccounts'}
                    title={'사용자 계정관리'}
                    tapIndex={0}
                    active={accountIndex === 0}
                  />
                  <DropMenuItem
                    link={'/managementboard'}
                    title={'학습보드 권한관리'}
                    tapIndex={1}
                    active={accountIndex === 1}
                  />
                </DropMenu>

                <DropMenu
                  icon={'bi-question-circle-fill'}
                  title={'데이터 다운로드'}
                  setTapIndex={setTapIndex}
                  tapIndex={5}
                  active={tapIndex === 5}
                >
                  <DropMenuItem
                    title={'전체 출석 다운로드'}
                    tapIndex={0}
                    onClick={handleDownloadAllAttendance}
                    active={accountIndex === 0}
                  />
                  <DropMenuItem
                    title={'전체 성적 다운로드'}
                    tapIndex={1}
                    onClick={handleDownloadAllGrade}
                    active={accountIndex === 1}
                  />
                  <DropMenuItem
                    title={'전체 설문 다운로드'}
                    tapIndex={1}
                    onClick={handleDownloadAllSurvey}
                    active={accountIndex === 1}
                  />
                </DropMenu>

                <DropMenu
                  icon={'bi-question-circle-fill'}
                  title={'데이터 업로드'}
                  setTapIndex={setTapIndex}
                  tapIndex={9}
                  active={tapIndex === 9}
                >
                  <DropMenuItem
                    title={'출석 업로드'}
                    tapIndex={0}
                    onClick={() => {
                      if (!subjectState.openYy || !subjectState.openTmGbn) {
                        alert('학기가 선택되지 않았습니다.');
                        return;
                      }
                      setVisibleExcelUploadPopup(true);
                    }}
                    active={false}
                  />
                </DropMenu>
              </div>
            )}
          </div>

          <div className='mt-150'>
            <ul>
              {/* <DropMenu
                icon={'bi-question-circle-fill'}
                title={'지원센터'}
                setTapIndex={setTapIndex}
                tapIndex={5}
                active={tapIndex === 5}
              >
                <DropMenuItem link={'/notice'} title={'공지사항'} tapIndex={0} active={supportingIndex === 0} />
                <DropMenuItem link={'/faq'} title={'FAQ'} tapIndex={1} active={supportingIndex === 1} />
                <DropMenuItem link={'/reference'} title={'자료실'} tapIndex={2} active={supportingIndex === 2} />
                <DropMenuItem link={'/manual'} title={'매뉴얼'} tapIndex={3} active={supportingIndex === 3} />
              </DropMenu> */}

              <MenuItem
                link={'/setting'}
                icon={'setting_icon.png'}
                title={'계정 설정'}
                setTapIndex={setTapIndex}
                tapIndex={6}
                active={tapIndex === 6}
              />
            </ul>
          </div>
        </div>
      </aside>
    </>
  );
}
